/**
 * About Route
 * Comprehensive about page with company information and mission
 */

import { createFileRoute } from '@tanstack/react-router';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateOrganizationSchema, generateBreadcrumbSchema } from '@/components/seo/StructuredData';
import { PageLayout } from '@/components/layout/PageLayout';

function AboutPage() {
  const organizationData = generateOrganizationSchema();

  const breadcrumbData = generateBreadcrumbSchema([
    { name: "Home", url: "https://invoicegenerated.com/" },
    { name: "About", url: "https://invoicegenerated.com/about" }
  ]);

  return (
    <>
      <SEOHead
        title="About Us - Smart Invoice Generator | Free AI-Powered Invoice Creator"
        description="Learn about Smart Invoice Generator - your free AI-powered invoice creator. Discover our mission to help freelancers and small businesses create professional invoices instantly without account requirements or hidden fees."
        keywords="about smart invoice generator, invoice maker story, free invoice tool, AI invoice creator mission, invoice generator team"
        canonical="https://invoicegenerated.com/about"
      />
      <StructuredData data={organizationData} />
      <StructuredData data={breadcrumbData} />

      <PageLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 sm:p-12">
            {/* Page Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              About Smart Invoice Generator
            </h1>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Smart Invoice Generator is a free, AI-powered tool designed to help freelancers, small business owners, and entrepreneurs create professional invoices in seconds. We believe that managing your business finances should be simple, fast, and accessible to everyone.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Our mission is to eliminate the complexity and cost associated with traditional invoicing software by providing an intelligent, user-friendly solution that requires no registration, no subscription fees, and no hidden costs.
              </p>
            </section>

            {/* Our Mission */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Our Mission
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We're on a mission to democratize professional invoicing. Every freelancer, consultant, and small business deserves access to high-quality invoicing tools without breaking the bank. Our AI-powered platform intelligently fills in details, calculates totals automatically, and ensures your invoices look professional every time.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                By removing barriers like account creation, credit card requirements, and complex feature sets, we make it possible for anyone to create and send professional invoices in under 60 seconds.
              </p>
            </section>

            {/* Why We Built This */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Why We Built Smart Invoice Generator
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                After years of watching freelancers and small businesses struggle with expensive, overcomplicated invoicing software, we knew there had to be a better way. Traditional invoice generators often require:
              </p>
              <ul className="list-disc list-inside space-y-3 text-gray-700 dark:text-gray-300 mb-6">
                <li>
                  <strong>Lengthy sign-up processes</strong> - Creating accounts and verifying email addresses just to send one invoice
                </li>
                <li>
                  <strong>Expensive monthly subscriptions</strong> - Paying $20-$50 per month for features you'll never use
                </li>
                <li>
                  <strong>Complex interfaces</strong> - Navigating through dozens of screens to create a simple document
                </li>
                <li>
                  <strong>Limited customization</strong> - Being forced into rigid templates that don't match your brand
                </li>
                <li>
                  <strong>Poor mobile experience</strong> - Trying to create invoices on-the-go with tools that aren't mobile-friendly
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Smart Invoice Generator solves all these problems. No account needed. No credit card required. Just open the page, fill in your details, and download your professional invoice instantly.
              </p>
            </section>

            {/* Key Features */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                What Makes Us Different
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    100% Free Forever
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    No hidden fees, no premium tiers, no credit card required. Generate unlimited invoices at zero cost.
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    AI-Powered Intelligence
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Our AI intelligently parses your input, calculates totals, applies discounts and taxes automatically, and formats everything perfectly.
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    No Account Required
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Start creating invoices immediately. No sign-up, no password to remember, no email verification needed.
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Multiple Export Formats
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Export as PDF for professional printing or PNG for quick sharing via email, messaging apps, or social media.
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Multi-Currency Support
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Choose from 150+ world currencies including USD, EUR, GBP, JPY, INR, AUD, CAD, and many more.
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Mobile-First Design
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Create professional invoices from anywhere using your phone, tablet, or computer. Works perfectly on all devices.
                  </p>
                </div>
              </div>
            </section>

            {/* Who Should Use This */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Perfect For Professionals Everywhere
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Smart Invoice Generator is designed for anyone who needs to create professional invoices quickly and easily:
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Freelancers & Consultants</h4>
                    <p className="text-gray-700 dark:text-gray-300">Web developers, designers, writers, marketing consultants, coaches, and independent professionals who bill by project or hour.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Small Business Owners</h4>
                    <p className="text-gray-700 dark:text-gray-300">Retailers, service providers, repair shops, tutors, and local businesses who need simple, professional invoicing without expensive software.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Contractors & Tradespeople</h4>
                    <p className="text-gray-700 dark:text-gray-300">Plumbers, electricians, carpenters, landscapers, and other contractors who need to invoice clients on the go.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Creative Professionals</h4>
                    <p className="text-gray-700 dark:text-gray-300">Photographers, videographers, musicians, artists, and content creators who need professional billing for their creative services.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Startups & Entrepreneurs</h4>
                    <p className="text-gray-700 dark:text-gray-300">Early-stage startups and solopreneurs who need professional invoicing without the overhead of complex accounting systems.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Our Commitment */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Our Commitment to You
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                We're committed to continuously improving Smart Invoice Generator based on your feedback. Here's what you can expect from us:
              </p>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                  <span><strong>Regular Updates:</strong> We continuously add new features, currencies, and improvements based on user feedback.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                  <span><strong>Privacy First:</strong> We don't store your invoice data on our servers. Your information stays on your device.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                  <span><strong>Always Free:</strong> We're committed to keeping the core invoice generator free forever for everyone.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                  <span><strong>Responsive Support:</strong> We value your feedback and are here to help if you have any questions or issues.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                  <span><strong>Accessibility:</strong> We're working to make our tool usable by everyone, regardless of ability or device.</span>
                </li>
              </ul>
            </section>

            {/* Get Started CTA */}
            <section className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-lg text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Ready to Create Your First Invoice?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg">
                Join thousands of freelancers and businesses who trust Smart Invoice Generator for their billing needs.
              </p>
              <a
                href="/"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                Create Free Invoice Now
              </a>
            </section>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export const Route = createFileRoute('/about')({
  component: AboutPage,
});
